import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Membership, User } from '../services/user'
import { createSelectors } from './utils'

type AuthState = {
  token: string
  hasHydrated: Promise<boolean>
  userId: string
  /**
   * @deprecated This is a copy of the user model, we keep this in local storage to have it readily available on app load. We will update it accordingly in time if the server user has changed. Do not use this as source of truth, use `useUser` from `src/services/user` instead
   */
  user: User | null
  isClearingAuth: boolean
  tokenExpirationDate: null | string
  /**
   * The ID of the organisation that is currently active in the UI.
   * Populated after login from the user's default organization (or first membership), or switched by the user.
   */
  activeOrgId: string | null
  /**
   * Flat list of permission codenames for the currently active organisation
   * (derived from the user's role in that org).
   * Example: ["expenses.access", "expenses.create", "receipts.access"]
   */
  activeOrgPermissions: string[]
  actions: {
    hydrate: () => void
    changeToken: (t: string) => void
    changeUserId: (id: string) => void
    clearAuth: () => void
    /**
     * Use only if you're syncing this state with the server
     * @deprecated
     */
    writeUserInStorage: (user: User) => void
    changeTokenExpirationDate: (tokenExpirationDate: string) => void
    /**
     * Set the active organisation from the user's memberships list.
     * Pass a membership object to switch orgs and update the permission list.
     */
    setActiveMembership: (membership: Membership) => void
  }
}
let resolveHydrationValue: (value: boolean) => void
const hasHydrated = new Promise<boolean>((res) => {
  resolveHydrationValue = res
})

const defaultValues: Omit<AuthState, 'actions' | 'hasHydrated'> = {
  token: '',
  userId: '',
  user: null,
  isClearingAuth: false,
  tokenExpirationDate: null,
  activeOrgId: null,
  activeOrgPermissions: [],
}

export const useAuth = createSelectors(
  create<AuthState>()(
    // This is a middleware that will auto-persist our changes in the store to the local storage. So anything that is within the store we will save into LocalStorage automagically.If there are any fields in this store that you don't want here you can customize the `partialize` callback further down
    persist(
      (set) => ({
        hasHydrated,
        ...defaultValues,
        actions: {
          changeToken(t) {
            set({ token: t, isClearingAuth: false })
          },
          changeUserId(id) {
            set({ userId: id })
          },
          hydrate() {
            resolveHydrationValue(true)
          },
          clearAuth() {
            set({ ...defaultValues })
          },
          writeUserInStorage(user) {
            set((state) => {
              // No active org yet: start from the user's default org, else the first membership
              const memberships = user.memberships ?? []
              if (!state.activeOrgId && memberships.length > 0) {
                const initial =
                  memberships.find((m) => m.organizationId === user.defaultOrganization) ??
                  memberships[0]
                return {
                  user,
                  activeOrgId: initial.organizationId,
                  activeOrgPermissions: initial.permissions,
                }
              }
              // If we already have an active org, refresh its permissions from the updated user
              if (state.activeOrgId) {
                const activeMembership = memberships.find(
                  (m) => m.organizationId === state.activeOrgId,
                )
                if (activeMembership) {
                  return {
                    user,
                    activeOrgPermissions: activeMembership.permissions,
                  }
                }
              }
              return { user }
            })
          },
          changeTokenExpirationDate(tokenExpirationDate) {
            set({ tokenExpirationDate })
          },
          setActiveMembership(membership) {
            set({
              activeOrgId: membership.organizationId,
              activeOrgPermissions: membership.permissions,
            })
          },
        },
      }),
      {
        name: 'auth',
        onRehydrateStorage: () => (state) => {
          if (!state) {
            return
          }
          // we're going to store this store in local storage so we must make sure that hydration succeeds
          state.actions.hydrate()
        },
        partialize(state) {
          const { actions: _, ...rest } = state
          return rest
        },
      },
    ),
  ),
)
